import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { H, MANROPE, FOCUS, BandChip, FLAG_LABELS, ddmon } from "../../healthcheck/hcTokens";
import HealthCheckForm from "../../healthcheck/HealthCheckForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/health-check`;

const Result = ({ r, companyName, onAnother }) => (
  <div className="rounded-[12px] overflow-hidden" data-testid="hc-staff-result"
    style={{ background: H.card, border: `1px solid ${H.border}` }}>
    <div className="px-5 py-4" style={{ borderBottom: `1px solid ${H.divider}`, background: H.panel }}>
      <div style={{ ...MANROPE, fontSize: 18, fontWeight: 800, color: H.ink }}>
        Health Check recorded — {companyName}
      </div>
      <div className="text-[13px] mt-0.5" style={{ color: H.ink2 }}>
        The client never sees the score. The follow-ups below are already in To-dos.
      </div>
    </div>
    <div className="px-5 py-5 grid sm:grid-cols-3 gap-5">
      <div>
        <div className="text-[10.5px] font-bold uppercase tracking-[0.1em]" style={{ color: H.ink3 }}>Score</div>
        <div className="mono" data-testid="hc-result-score"
          style={{ fontSize: 34, fontWeight: 800, color: H.ink, lineHeight: 1.1 }}>{r.score}<span className="text-[16px]" style={{ color: H.ink3 }}>/100</span></div>
      </div>
      <div>
        <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] mb-1.5" style={{ color: H.ink3 }}>Calculated band</div>
        <BandChip band={r.calculated_band} testid="hc-result-calc-band" />
      </div>
      <div>
        <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] mb-1.5" style={{ color: H.ink3 }}>Final band</div>
        <BandChip band={r.final_band} testid="hc-result-final-band" />
      </div>
    </div>
    {(r.flags || []).length > 0 && (
      <div className="px-5 pb-4 flex gap-2 flex-wrap">
        {r.flags.map((f) => (
          <span key={f} className="text-[11.5px] px-2 py-0.5 rounded-full" data-testid={`hc-result-flag-${f}`}
            style={{ background: H.sunken, border: `1px solid ${H.divider}`, color: H.ink2 }}>
            {FLAG_LABELS[f] || f}
          </span>
        ))}
      </div>
    )}
    <div className="px-5 py-4" style={{ borderTop: `1px solid ${H.divider}` }}>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] mb-2" style={{ color: H.ink3 }}>
        Follow-ups created
      </div>
      {(r.created?.todos || []).length === 0 && (r.created?.backlog || []).length === 0 ? (
        <div className="text-[13px]" style={{ color: H.ink2 }}>Nothing needed — Green on the normal cadence.</div>
      ) : (
        <div className="space-y-1.5">
          {(r.created?.todos || []).map((t) => (
            <div key={t.id} className="text-[13px]" style={{ color: H.ink }} data-testid={`hc-created-todo-${t.id}`}>
              {t.title} <span style={{ color: H.ink3 }}>· {t.owner || "Unassigned"} · due {ddmon(t.due_date)}</span>
            </div>
          ))}
          {(r.created?.backlog || []).map((b) => (
            <div key={b.id} className="text-[13px]" style={{ color: H.ink }}>
              {b.kind === "feature_request" ? "Feature ask" : "Pain point"} logged — {b.text}
              <span style={{ color: H.ink3 }}> · answer due in 30 days</span>
            </div>
          ))}
        </div>
      )}
    </div>
    <div className="px-5 py-4 flex gap-2" style={{ borderTop: `1px solid ${H.divider}`, background: H.panel }}>
      <a href={`/health-checks/${r.submission_id}`} data-testid="hc-result-open"
        className="h-10 px-4 rounded-[8px] text-[12px] font-bold uppercase inline-flex items-center"
        style={{ background: H.accent, border: `1px solid ${H.accentEdge}`, color: "#111", letterSpacing: "0.05em" }}>
        Open the submission
      </a>
      <button onClick={onAnother} data-testid="hc-result-another"
        className="h-10 px-4 rounded-[8px] text-[12px] font-bold uppercase"
        style={{ background: "#fff", border: `1px solid ${H.control}`, color: H.ink2, letterSpacing: "0.05em" }}>
        Back to the client
      </button>
    </div>
  </div>
);

export default function HealthCheckStaffPage({ recordId }) {
  const [form, setForm] = useState(null);
  const [a, setA] = useState({});
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState([]);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/staff/form/${encodeURIComponent(recordId)}`);
      setForm(r.data);
      setA({ ...(r.data.prefill || {}), G8: "No override", pain_points: [{}] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the Health Check");
    }
  }, [recordId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setProblems([]); setBusy(true);
    try {
      const r = await axios.post(`${API}/staff/submit`, { client_id: recordId, answers: a });
      setResult(r.data);
      toast.success(`Recorded — ${r.data.score}/100, ${r.data.final_band}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setProblems([typeof d === "string" ? d : "Could not record the check"]);
    } finally { setBusy(false); }
  };

  if (!form) return <div className="px-6 py-6 text-[13px]" style={{ color: H.ink2 }}>Loading…</div>;

  return (
    <div className="px-1 pb-8" data-testid="hc-staff-page">
      <div className="text-[11.5px] mb-2.5">
        <a href="/customer-success/clients" style={{ color: H.accentInk }}>Clients</a>
        <span style={{ color: H.ink3 }}> / </span>
        <a href={`/clients/${form.client_id}`} style={{ color: H.accentInk }}>{form.company_name}</a>
        <span style={{ color: H.ink3 }}> / </span>
        <span style={{ color: H.ink, fontWeight: 600 }}>Health Check</span>
      </div>
      <h1 className="mb-1" style={{ ...MANROPE, fontSize: 25, fontWeight: 800, letterSpacing: "-0.018em", color: H.ink }}
        data-testid="hc-staff-title">
        Health Check — {form.company_name}
      </h1>
      <div className="text-[13px] mb-5" style={{ color: H.ink2 }}>
        Work through it on the call. Sections A to F are the client's words; Section G is ours.
      </div>

      {result ? (
        <div className="max-w-[760px]">
          <Result r={result} companyName={form.company_name}
            onAnother={() => { window.location.href = `/clients/${form.client_id}`; }} />
        </div>
      ) : (
        <div className="max-w-[760px] rounded-[12px] px-5 py-6 sm:px-7"
          style={{ background: H.card, border: `1px solid ${H.border}` }}>
          <HealthCheckForm mode="staff" clientId={form.client_id} companyName={form.company_name}
            meta={form.meta} a={a} setA={setA} problems={problems} busy={busy} onSubmit={submit} />
        </div>
      )}
    </div>
  );
}
